import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Space,
  Tabs,
  Typography,
  message,
} from 'antd';
import {
  LockOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authApi, authStore, type AuthConfig } from '@/services/api';

const { Text, Paragraph } = Typography;

const REMEMBER_KEY = 'edupaper_remember_username';

interface LoginForm {
  username: string;
  password: string;
  remember?: boolean;
}

interface RegisterForm {
  username: string;
  password: string;
  confirm: string;
  displayName?: string;
  email?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [loginForm] = Form.useForm<LoginForm>();
  const [registerForm] = Form.useForm<RegisterForm>();

  // 读取公开的鉴权策略：决定是否显示注册页签、密码规则
  useEffect(() => {
    authApi
      .getConfig()
      .then((res: any) => setConfig(res?.data ?? null))
      .catch(() => setConfig(null));

    const remembered = localStorage.getItem(REMEMBER_KEY);
    if (remembered) {
      loginForm.setFieldsValue({ username: remembered, remember: true });
    }
  }, [loginForm]);

  // 鉴权未开启时无需登录，直接进入系统
  useEffect(() => {
    if (config && config.authRequired === false) {
      navigate('/dashboard', { replace: true });
    }
  }, [config, navigate]);

  const handleLogin = async (values: LoginForm) => {
    setLoading(true);
    try {
      const res: any = await authApi.login({
        username: values.username.trim(),
        password: values.password,
      });
      const payload = res?.data ?? res;
      if (!payload?.accessToken) {
        throw new Error('登录响应缺少令牌');
      }
      authStore.save(payload.accessToken, payload.user);
      if (values.remember) {
        localStorage.setItem(REMEMBER_KEY, values.username.trim());
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      message.success(`欢迎回来，${payload.user?.displayName || payload.user?.username || ''}`);
      navigate('/dashboard', { replace: true });
    } catch {
      // 错误提示由 http 拦截器统一给出
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: RegisterForm) => {
    if (values.password !== values.confirm) {
      message.error('两次输入的密码不一致');
      return;
    }
    setLoading(true);
    try {
      const res: any = await authApi.register({
        username: values.username.trim(),
        password: values.password,
        displayName: values.displayName,
        email: values.email,
      });
      const payload = res?.data ?? res;
      if (payload?.accessToken) {
        authStore.save(payload.accessToken, payload.user);
        message.success('注册成功，已自动登录');
        navigate('/dashboard', { replace: true });
      } else {
        message.success('注册成功，请登录');
        setTab('login');
        loginForm.setFieldsValue({ username: values.username.trim() });
      }
    } catch {
      // 错误提示由 http 拦截器统一给出
    } finally {
      setLoading(false);
    }
  };

  const minPwd = config?.passwordRules?.minLength ?? 6;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e6f4ff 0%, #f0f5ff 45%, #f9f0ff 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{ width: 880, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ display: 'flex', minHeight: 460 }}>
          {/* ── 左侧品牌区 ── */}
          <div
            style={{
              width: 380,
              padding: '48px 36px',
              background: 'linear-gradient(160deg, #1677ff 0%, #2f54eb 60%, #722ed1 100%)',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Space align="center" size={10}>
                <RobotOutlined style={{ fontSize: 30 }} />
                <span style={{ fontSize: 22, fontWeight: 600 }}>腾岳Edu AI</span>
              </Space>
              <Paragraph style={{ color: 'rgba(255,255,255,0.85)', marginTop: 20, fontSize: 14 }}>
                面向教师的智能组卷平台：题库管理、智能组卷、AI 出题、AI 教案、讲评 PPT，
                一站式完成。
              </Paragraph>
            </div>

            <div>
              <Divider style={{ borderColor: 'rgba(255,255,255,0.25)', margin: '24px 0' }} />
              <Space direction="vertical" size={8} style={{ fontSize: 13 }}>
                <span>
                  <SafetyCertificateOutlined /> 多用户隔离，数据仅本人可见
                </span>
                <span>
                  <LockOutlined /> JWT 鉴权，按角色控制权限
                </span>
              </Space>
            </div>
          </div>

          {/* ── 右侧表单区 ── */}
          <div style={{ flex: 1, padding: '40px 44px' }}>
            <Tabs
              activeKey={tab}
              onChange={(k) => setTab(k as 'login' | 'register')}
              items={[
                { key: 'login', label: '登录' },
                ...(config?.openRegistration ? [{ key: 'register', label: '注册' }] : []),
              ]}
            />

            {tab === 'login' ? (
              <Form
                form={loginForm}
                layout="vertical"
                onFinish={handleLogin}
                requiredMark={false}
                size="large"
              >
                <Form.Item
                  label="用户名"
                  name="username"
                  rules={[{ required: true, message: '请输入用户名' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="请输入用户名"
                    autoComplete="username"
                  />
                </Form.Item>

                <Form.Item
                  label="密码"
                  name="password"
                  rules={[{ required: true, message: '请输入密码' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="请输入密码"
                    autoComplete="current-password"
                  />
                </Form.Item>

                <Form.Item name="remember" valuePropName="checked" style={{ marginBottom: 16 }}>
                  <Checkbox>记住用户名</Checkbox>
                </Form.Item>

                <Button type="primary" htmlType="submit" block loading={loading} size="large">
                  登录
                </Button>
              </Form>
            ) : (
              <Form
                form={registerForm}
                layout="vertical"
                onFinish={handleRegister}
                requiredMark={false}
                size="large"
              >
                <Form.Item
                  label="用户名"
                  name="username"
                  rules={[
                    { required: true, message: '请输入用户名' },
                    { min: 3, max: 50, message: '用户名长度 3-50 位' },
                  ]}
                >
                  <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="3-50 位，用于登录" />
                </Form.Item>

                <Form.Item label="显示名" name="displayName">
                  <Input placeholder="选填，例如：张老师" />
                </Form.Item>

                <Form.Item
                  label="密码"
                  name="password"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: minPwd, message: `密码至少 ${minPwd} 位` },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder={`至少 ${minPwd} 位`}
                  />
                </Form.Item>

                <Form.Item
                  label="确认密码"
                  name="confirm"
                  dependencies={['password']}
                  rules={[{ required: true, message: '请再次输入密码' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="再次输入密码"
                  />
                </Form.Item>

                <Button type="primary" htmlType="submit" block loading={loading} size="large">
                  注册并登录
                </Button>
              </Form>
            )}

            {tab === 'register' && !config?.openRegistration && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                当前已关闭自助注册，请联系管理员创建账号。
              </Text>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Login;
